import React, { useEffect, useState, useRef } from 'react';
import { Plus, Minus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
function stripBullets(text: string): string {
  return text.split('\n').map(line => line.replace(/^•\s*/, '').trim()).filter(Boolean).join(' ');
}
function renderAnswer(text: string) {
  const lines = text.split('\n').filter(Boolean);
  return <div className="space-y-2" data-id="element-113">
      {lines.map((line, i) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('•')) {
        return <div key={i} className="flex items-start gap-2 pl-1" data-id="element-114">
              <span className="text-accent-blue mt-1 text-xs" data-id="element-115">•</span>
              <span className="text-gray-400 leading-relaxed text-sm" data-id="element-116">
                {trimmed.slice(1).trim()}
              </span>
            </div>;
      }
      return <p key={i} className="text-gray-400 leading-relaxed text-sm" data-id="element-117">
            {trimmed}
          </p>;
    })}
    </div>;
}
export function FAQ() {
  const {
    t,
    language
  } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = t.faq.items || [];
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useScrollAnimation();
  useEffect(() => {
    setOpenIndex(null);
  }, [language]);
  // JSON-LD removed — FAQPage schema is managed centrally in LandingPage.tsx
  // to avoid duplicate structured data warnings in Google Search Console
  return <section id="faq" className="py-24 bg-[#050505]" data-id="element-118">
      {/*
          Noscript fallback: renders FAQ content as plain HTML so crawlers
          that don't execute JS can still read questions and answers.
          No display:none — content must be visible when JS is disabled.
        */}
      <noscript data-id="element-119">
        <div itemScope itemType="https://schema.org/FAQPage" data-id="element-120">
          {faqs.map((faq: any, i: number) => <div key={i} itemScope itemType="https://schema.org/Question" data-id="element-121">
              <h3 itemProp="name" data-id="element-122">{faq.q}</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer" data-id="element-123">
                <span itemProp="text" data-id="element-124">{stripBullets(faq.a)}</span>
              </div>
            </div>)}
        </div>
      </noscript>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-125">
        <div ref={titleRef} className={`text-center mb-12 animate-hidden animate-fade-up ${titleVisible ? 'animate-visible' : ''}`} data-id="element-126">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-127">
            {t.faq.title}
          </h2>
          <p className="text-lg text-gray-400" data-id="element-128">{t.faq.subtitle}</p>
        </div>

        <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage" data-id="element-129">
          {faqs.map((faq: any, index: number) => {
          return <FAQItem key={`${language}-${index}`} faq={faq} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} index={index} data-id="element-130" />;
        })}
        </div>
      </div>
    </section>;
}
function FAQItem({
  faq,
  isOpen,
  onToggle,
  index
}: {
  faq: any;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const {
    ref: itemRef,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const delayClass = index < 5 ? `delay-${(index + 1) * 100}` : '';
  return <div ref={itemRef} className={`border border-white/10 rounded-xl bg-[#0A0A0A] overflow-hidden animate-hidden animate-fade-up ${isVisible ? 'animate-visible' : ''} ${delayClass}`} itemScope itemType="https://schema.org/Question" data-id="element-131">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 text-left focus:outline-none" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} data-id="element-132">
        <h3 className={`font-semibold text-base pr-6 transition-colors duration-200 ${isOpen ? 'text-accent-blue' : 'text-white'}`} itemProp="name" data-id="element-133">
          {faq.q}
        </h3>
        <div className={`p-1.5 rounded-full shrink-0 transition-colors duration-200 ${isOpen ? 'bg-accent-blue text-white' : 'bg-accent-blue/10 text-accent-blue'}`} data-id="element-134">
          {isOpen ? <Minus className="w-3.5 h-3.5" data-id="element-135" /> : <Plus className="w-3.5 h-3.5" data-id="element-136" />}
        </div>
      </button>

      <div id={`faq-answer-${index}`} ref={contentRef} className="transition-[max-height,opacity] duration-300 ease-out overflow-hidden" style={{
      maxHeight: isOpen ? contentRef.current?.scrollHeight : 0,
      opacity: isOpen ? 1 : 0
    }} itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer" data-id="element-137">
        <div className="px-5 pb-5" itemProp="text" data-id="element-138">
          {renderAnswer(faq.a)}
        </div>
      </div>
    </div>;
}